import { Badge, Button, Select } from '@chopchop-se/ui';
import { useQuery } from '@tanstack/react-query';
import type { ArticleListItem } from '@warehouse/shared';
import { Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { usePermission } from '../../../auth/auth-store';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { formatDateTime, formatQty } from '../../../lib/format';
import { listQueryKey } from '../../../lib/list-query';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { listArticles } from '../api';
import { BcBadge } from '../components/BcBadge';
import { ArticleFormModal } from './ArticleFormModal';
import { useBooleanFilterOptions, useCategories, useSuppliers } from '../lookups';

/**
 * The article register (spec §5.3).
 *
 * Search, filters, sort and paging are all SERVER-side through the shared
 * list-query contract, and the state lives in the URL — a 40 000-row register
 * cannot be sorted in the browser, and "the blocked, non-orderable articles for
 * supplier X" has to be a link someone can paste.
 */
export function ArticlesPage(): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const navigate = useNavigate();
  const { state, setState, setFilter } = useListState({ defaultSort: 'articleNo', defaultDir: 'asc' });
  const [creating, setCreating] = useState(false);

  const canManage = usePermission('registers.articles.manage');
  const { options: categoryOptions } = useCategories();
  const { options: supplierOptions } = useSuppliers();
  const booleanOptions = useBooleanFilterOptions();

  const queryKey = ['master-data', 'articles', listQueryKey(state)];
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => listArticles(state),
  });
  useLiveQuery(['articles'], ['master-data', 'articles']);

  const columns: DataTableColumn<ArticleListItem>[] = [
    {
      id: 'articleNo',
      header: t('masterData:articles.columns.articleNo'),
      sortField: 'articleNo',
      width: 'w-44',
      cell: (row) => <span className="font-mono text-sm">{row.articleNo}</span>,
    },
    {
      id: 'name',
      header: t('masterData:articles.columns.name'),
      sortField: 'name',
      cell: (row) => (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-ink">{row.name}</span>
          {row.bcSynced ? <BcBadge bc={{ synced: true, bcNo: null }} /> : null}
        </div>
      ),
    },
    {
      id: 'category',
      header: t('masterData:articles.columns.category'),
      sortField: 'categoryName',
      hideBelow: 'md',
      cell: (row) => row.categoryName ?? <span className="text-ink-muted">{t('masterData:articles.noCategory')}</span>,
    },
    {
      id: 'supplier',
      header: t('masterData:articles.columns.supplier'),
      sortField: 'supplierName',
      hideBelow: 'lg',
      cell: (row) => row.supplierName ?? <span className="text-ink-muted">{t('masterData:articles.noSupplier')}</span>,
    },
    {
      id: 'baseUnit',
      header: t('masterData:articles.columns.baseUnit'),
      hideBelow: 'lg',
      cell: (row) => <span className="font-mono text-xs">{row.baseUnitCode}</span>,
    },
    {
      id: 'priority',
      header: t('masterData:articles.columns.priority'),
      sortField: 'priority',
      align: 'right',
      hideBelow: 'xl',
      cell: (row) => formatQty(row.priority, { decimals: 0 }),
    },
    {
      id: 'barcodes',
      header: t('masterData:articles.columns.barcodes'),
      align: 'right',
      hideBelow: 'xl',
      cell: (row) => row.barcodeCount,
    },
    {
      id: 'orderable',
      header: t('masterData:articles.columns.orderable'),
      sortField: 'orderable',
      cell: (row) =>
        row.orderable ? (
          <Badge tone="success" dot>
            {t('common:common.yes')}
          </Badge>
        ) : (
          <Badge tone="neutral" dot>
            {t('common:common.no')}
          </Badge>
        ),
    },
    {
      id: 'active',
      header: t('masterData:articles.columns.active'),
      sortField: 'isActive',
      hideBelow: 'md',
      cell: (row) =>
        row.isActive ? (
          <Badge tone="success">{t('common:common.active')}</Badge>
        ) : (
          <Badge tone="warning">{t('common:common.inactive')}</Badge>
        ),
    },
    {
      id: 'updatedAt',
      header: t('masterData:articles.columns.updatedAt'),
      sortField: 'updatedAt',
      hideBelow: 'xl',
      cell: (row) => <span className="text-xs text-ink-muted">{formatDateTime(row.updatedAt)}</span>,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('masterData:articles.title')}
        subtitle={t('masterData:articles.subtitle')}
        actions={
          canManage ? (
            <Button
              icon={<Plus />}
              onClick={() => {
                setCreating(true);
              }}
            >
              {t('masterData:articles.new')}
            </Button>
          ) : null
        }
      />

      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={data?.total ?? null}
        loading={isLoading}
        error={error}
        searchPlaceholder={t('masterData:articles.searchPlaceholder')}
        emptyTitle={t('masterData:articles.empty')}
        emptyDescription={t('masterData:articles.emptyDescription')}
        onRowClick={(row) => {
          void navigate(`/registers/articles/${row.id}`);
        }}
        filters={
          <>
            <Select
              aria-label={t('masterData:articles.filters.category')}
              className="w-48"
              options={[{ value: '', label: t('masterData:articles.filters.category') }, ...categoryOptions]}
              value={String(state.filters?.categoryId ?? '')}
              onChange={(event) => {
                setFilter('categoryId', event.target.value || undefined);
              }}
            />
            <Select
              aria-label={t('masterData:articles.filters.supplier')}
              className="w-48"
              options={[{ value: '', label: t('masterData:articles.filters.supplier') }, ...supplierOptions]}
              value={String(state.filters?.supplierId ?? '')}
              onChange={(event) => {
                setFilter('supplierId', event.target.value || undefined);
              }}
            />
            <Select
              aria-label={t('masterData:articles.filters.orderable')}
              className="w-40"
              options={booleanOptions.map((option) =>
                option.value === '' ? { ...option, label: t('masterData:articles.filters.orderable') } : option,
              )}
              value={String(state.filters?.orderable ?? '')}
              onChange={(event) => {
                setFilter('orderable', event.target.value === '' ? undefined : event.target.value === 'true');
              }}
            />
            <Select
              aria-label={t('masterData:articles.filters.active')}
              className="w-40"
              options={booleanOptions.map((option) =>
                option.value === '' ? { ...option, label: t('masterData:articles.filters.active') } : option,
              )}
              value={String(state.filters?.isActive ?? '')}
              onChange={(event) => {
                setFilter('isActive', event.target.value === '' ? undefined : event.target.value === 'true');
              }}
            />
          </>
        }
      />

      {creating ? (
        <ArticleFormModal
          onClose={() => {
            setCreating(false);
          }}
          onCreated={(article) => {
            setCreating(false);
            void navigate(`/registers/articles/${article.id}`);
          }}
        />
      ) : null}
    </>
  );
}
